import { useCallback, useEffect, useMemo, useState } from 'react';
import PassoPagamento from '@/pages/assistente-evento/components/PassoPagamento';
import CartaoTaxas from '@/pages/gerenciar/loja/components/CartaoTaxas';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { eventoDraftVazio } from '@/pages/assistente-evento/utils';

interface Props {
  eventoId: number;
  taxas: { loja: string | null; pix: string | null; cartao: string | null; parcelado: string | null };
  mostrarParcial: boolean;
  aoSalvo: () => void;
}

interface ModParcial {
  id: number;
  nome: string;
  permite_pagamento_parcial: boolean;
  exige_quitacao_para_confirmar: boolean;
  quitacao_ate: string | null;
}

function dataDe(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Permite parcial · confirma só ao quitar · quitar até — por tipo de inscrição.
function PagamentoParcial({ eventoId }: { eventoId: number }) {
  const [mods, setMods] = useState<ModParcial[]>([]);
  const [salvando, setSalvando] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const { data, error } = await supabase
      .from('modalidades')
      .select('id, nome, permite_pagamento_parcial, exige_quitacao_para_confirmar, quitacao_ate')
      .eq('evento_id', eventoId)
      .order('ordem');
    if (error) {
      setErro(mensagemAmigavel(error));
      return;
    }
    setMods((data as ModParcial[]) || []);
  }, [eventoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function salvar(id: number, patch: Partial<ModParcial>) {
    setSalvando(id);
    setErro(null);
    try {
      const { error } = await supabase.from('modalidades').update(patch).eq('id', id);
      if (error) throw error;
      setMods((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(null);
    }
  }

  if (mods.length === 0) return null;

  return (
    <section className="rounded-lg border border-secondary-200 bg-white p-5">
      <h3 className="text-base font-medium text-foreground-950">Pagamento parcelado</h3>
      <p className="mt-1 text-sm text-secondary-500">
        Permita pagar por partes. Só confirma a inscrição quando quitar, se quiser.
      </p>

      {erro && <p className="mt-2 text-sm text-danger-600">{erro}</p>}

      <div className="mt-4 space-y-4 divide-y divide-secondary-100">
        {mods.map((m) => (
          <div key={m.id} className="pt-3 first:pt-0">
            <p className="text-sm font-medium text-foreground-950">{m.nome}</p>
            <label className="mt-2 flex items-start gap-2.5">
              <input
                type="checkbox"
                checked={m.permite_pagamento_parcial}
                disabled={salvando === m.id}
                onChange={(e) => salvar(m.id, { permite_pagamento_parcial: e.target.checked })}
                className="mt-0.5 h-4 w-4 cursor-pointer accent-primary-500"
              />
              <span className="text-sm text-foreground-800">Permite pagamento parcial</span>
            </label>

            {m.permite_pagamento_parcial && (
              <div className="mt-3 pl-6">
                <label className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={m.exige_quitacao_para_confirmar}
                    disabled={salvando === m.id}
                    onChange={(e) =>
                      salvar(m.id, { exige_quitacao_para_confirmar: e.target.checked })
                    }
                    className="mt-0.5 h-4 w-4 cursor-pointer accent-primary-500"
                  />
                  <span className="text-sm text-foreground-800">
                    Só confirma a inscrição ao quitar
                  </span>
                </label>

                <label className="mt-2 block">
                  <span className="text-xs text-secondary-500">Quitar até</span>
                  <input
                    type="date"
                    value={dataDe(m.quitacao_ate)}
                    disabled={salvando === m.id}
                    onChange={(e) =>
                      salvar(m.id, {
                        quitacao_ate: e.target.value
                          ? new Date(`${e.target.value}T23:59:00`).toISOString()
                          : null,
                      })
                    }
                    className="mt-1 w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none focus:border-primary-500 sm:w-48"
                  />
                </label>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function DinheiroEditor({ eventoId, taxas, mostrarParcial, aoSalvo }: Props) {
  // O PassoPagamento só usa o draft no modo legado; aqui ele grava sempre por RPC.
  const draft = useMemo(() => eventoDraftVazio(), []);

  return (
    <div className="space-y-5">
      <PassoPagamento eventoId={eventoId} draft={draft} onChange={() => {}} />

      <CartaoTaxas
        eventoId={eventoId}
        loja={taxas.loja}
        pix={taxas.pix}
        cartao={taxas.cartao}
        parcelado={taxas.parcelado}
        aoSalvo={aoSalvo}
      />

      {mostrarParcial && <PagamentoParcial eventoId={eventoId} />}

      <div className="sticky bottom-0 -mx-5 flex gap-2 border-t border-secondary-100 bg-white px-5 py-3">
        <button
          type="button"
          onClick={aoSalvo}
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
        >
          Concluir
        </button>
      </div>
    </div>
  );
}